import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, User, LogOut, CreditCard, MessageCircle, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParentDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddChild, setShowAddChild] = useState(false);
  const [childForm, setChildForm] = useState({
    name: '',
    grade: 'Class 1',
    age: 6,
    create_login: false,
    password: ''
  });

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/parent`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/children`, childForm, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Child added successfully!');
      setShowAddChild(false);
      setChildForm({ name: '', grade: 'Class 1', age: 6, create_login: false, password: '' });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to add child');
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const viewWeeklyReport = async (childId) => {
    try {
      const response = await axios.get(`${API}/reports/weekly/${childId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Generating weekly report...');
      console.log('Weekly Report:', response.data.report);
      // You can create a modal to display this report
      alert(JSON.stringify(response.data.report, null, 2));
    } catch (error) {
      toast.error('Failed to generate report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              Welcome back, {user.name}!
            </h1>
            <p className="text-muted-foreground">Your family's learning journey</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="subscription-btn"
              variant="outline"
              onClick={() => navigate('/subscription')}
              className="rounded-full"
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Subscription
            </Button>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={handleLogout}
              className="rounded-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Children Grid */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Add Child Card */}
          <Dialog open={showAddChild} onOpenChange={setShowAddChild}>
            <DialogTrigger asChild>
              <div
                data-testid="add-child-card"
                className="card-interactive p-8 flex flex-col items-center justify-center min-h-64 cursor-pointer"
              >
                <Plus className="h-16 w-16 text-primary mb-4" />
                <h3 className="font-bold text-xl">Add a Child</h3>
                <p className="text-muted-foreground text-sm mt-2">Start their learning journey</p>
              </div>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Child</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddChild} className="space-y-4">
                <div>
                  <Label htmlFor="childName">Child's Name</Label>
                  <Input
                    id="childName"
                    data-testid="child-name-input"
                    value={childForm.name}
                    onChange={(e) => setChildForm({ ...childForm, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="childGrade">Grade</Label>
                  <Select
                    value={childForm.grade}
                    onValueChange={(value) => setChildForm({ ...childForm, grade: value })}
                  >
                    <SelectTrigger data-testid="child-grade-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5'].map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="childAge">Age</Label>
                  <Input
                    id="childAge"
                    data-testid="child-age-input"
                    type="number"
                    min="5"
                    max="12"
                    value={childForm.age}
                    onChange={(e) => setChildForm({ ...childForm, age: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div className="flex items-center space-x-2 p-4 border rounded-lg">
                  <input
                    type="checkbox"
                    id="createLogin"
                    checked={childForm.create_login}
                    onChange={(e) => setChildForm({ ...childForm, create_login: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="createLogin" className="cursor-pointer">
                    Enable child login (child can login independently)
                  </Label>
                </div>
                {childForm.create_login && (
                  <div>
                    <Label htmlFor="childPassword">Child's Password</Label>
                    <Input
                      id="childPassword"
                      type="password"
                      placeholder="Create password for child"
                      value={childForm.password}
                      onChange={(e) => setChildForm({ ...childForm, password: e.target.value })}
                      required={childForm.create_login}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Child will login with: <strong>{childForm.name.toLowerCase().replace(' ', '')}</strong>
                    </p>
                  </div>
                )}
                <Button data-testid="submit-child-btn" type="submit" className="w-full rounded-full">
                  Add Child
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Child Cards */}
          {dashboardData?.children?.map((item) => (
            <div
              key={item.child.child_id}
              data-testid={`child-card-${item.child.child_id}`}
              className="card-interactive p-8"
              onClick={() => navigate(`/child/dashboard/${item.child.child_id}`)}
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <div className="ml-4">
                  <h3 className="font-bold text-xl">{item.child.name}</h3>
                  <p className="text-sm text-muted-foreground">{item.child.grade}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Tests Completed</span>
                  <span className="font-bold">{item.stats.total_tests}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Average Score</span>
                  <span className="font-bold text-secondary">{item.stats.average_score}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Current Streak</span>
                  <span className="font-bold text-primary">{item.stats.streak} days</span>
                </div>
              </div>

              <Button className="w-full mt-6 rounded-full" variant="outline">
                View Dashboard
              </Button>
            </div>
          ))}
        </div>

        {/* Subscription Status */}
        {dashboardData?.subscription && (
          <div className="card-base p-8 mt-8">
            <h2 className="font-heading text-2xl font-bold mb-4">Subscription Status</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground">Plan</p>
                <p className="font-bold text-lg capitalize">{dashboardData.subscription.plan_type}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-bold text-lg text-secondary capitalize">{dashboardData.subscription.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tests Completed</p>
                <p className="font-bold text-lg">{dashboardData.subscription.tests_completed}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;